import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { brl, pct } from "@/lib/format";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/kit";

export const needsReprice = (r) => r.sale_price > 0 && r.suggested_price > 0 && r.sale_price < r.suggested_price - 0.004;

function Row({ r, checked, onToggle }) {
  return (
    <label className="flex items-center gap-3 py-2.5 cursor-pointer" data-testid={`reprice-row-${r.id}`}>
      <input type="checkbox" className="w-4 h-4 accent-[var(--brand)]" checked={checked} onChange={onToggle} data-testid={`reprice-check-${r.id}`} />
      <div className="flex-1 min-w-0">
        <div className="label truncate">{r.name}</div>
        <div className="caption tnum">CMV {pct(r.cmv_pct)} → {pct(r.cmv_target, 1)} · custo {brl(r.cost_per_portion)}</div>
      </div>
      <div className="text-right tnum shrink-0">
        <div className="caption line-through">{brl(r.sale_price)}</div>
        <div className="label" style={{ color: "var(--brand)" }} data-testid={`reprice-new-${r.id}`}>{brl(r.suggested_price)}</div>
      </div>
    </label>
  );
}

export default function RepriceDialog({ open, onOpenChange, recipes }) {
  const qc = useQueryClient();
  const list = useMemo(() => recipes.filter(needsReprice), [recipes]);
  const [sel, setSel] = useState([]);
  useEffect(() => { if (open) setSel(list.map((r) => r.id)); }, [open, list]);
  const apply = useMutation({
    mutationFn: () => api("/recipes/reprice", { method: "POST", body: { items: list.filter((r) => sel.includes(r.id)).map((r) => ({ id: r.id, sale_price: r.suggested_price })) } }),
    onSuccess: (d) => { qc.invalidateQueries({ queryKey: ["recipes"] }); qc.invalidateQueries({ queryKey: ["recipe"] }); toast.success(`${d.updated} preço(s) atualizado(s)`); onOpenChange(false); },
    onError: (e) => toast.error(e.message),
  });
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const all = list.length > 0 && sel.length === list.length;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col" data-testid="reprice-dialog">
        <DialogTitle>Reajustar preços</DialogTitle>
        <DialogDescription>Pratos com preço abaixo do sugerido pela meta de CMV · o novo preço é o valor exato sugerido.</DialogDescription>
        {!list.length ? <p className="caption py-4" data-testid="reprice-empty">Todos os pratos estão no preço sugerido ou acima.</p> : (
          <>
            <button type="button" className="caption self-start underline" onClick={() => setSel(all ? [] : list.map((r) => r.id))} data-testid="reprice-toggle-all">{all ? "Desmarcar todos" : "Marcar todos"}</button>
            <div className="divide overflow-y-auto -mx-1 px-1">{list.map((r) => <Row key={r.id} r={r} checked={sel.includes(r.id)} onToggle={() => toggle(r.id)} />)}</div>
            <Button icon={TrendingUp} loading={apply.isPending} disabled={!sel.length} onClick={() => window.confirm(`Atualizar o preço de venda de ${sel.length} prato(s)?\n\n${list.filter((r) => sel.includes(r.id)).map((r) => `• ${r.name}: ${brl(r.sale_price)} → ${brl(r.suggested_price)}`).join("\n")}`) && apply.mutate()} data-testid="reprice-apply-button">Aplicar em {sel.length} prato(s)</Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
